import { Box } from '@material-ui/core';
import { lighten, makeStyles, Theme } from '@material-ui/core/styles';
import { LoginLogoImg } from '@roc/feature-app-core';
import {
  formatDate as _formatDate,
  formatBorrowerExperience,
  LoanSubType,
  formatToLocalDateTimeString,
} from '@roc/feature-utils';
import { PrintableArea } from '@roc/ui';
import {
  formatCurrency as _formatCurrency,
  formatPercentage as _formatPercentage,
} from '@roc/ui/utils';
import clsx from 'clsx';
import { mapLtvToValue } from 'libs/feature-loans/src/loanSubmission/stores/common/pricerSummaryStore';
import { observer } from 'mobx-react';
import { forwardRef } from 'react';
import { DEFAULT_FEE_CORELOGIX, DEFAULT_FEE_RETAIL_SERVICING_SETUP } from 'libs/feature-loans/src/loanSubmission/utils/constants';
import {
  swapIndexLabels,
} from 'libs/feature-loans/src/rentalPricer/pricerSummary/utils/constants';
import { amortizationOptions } from '@roc/feature-loans';
import { QuoteTypeStore } from '../../stores/quoteTypeStore';
import { LoanQuoteData } from '../../types/LoanQuoteData';

const computeLiquidityNeeded = (option, store: QuoteTypeStore) => {
  if (!option) return 0;
  const loanAmount = option.pricerLoanTerms?.amount ?? 0;
  const monthlyPayment = option.pricerLoanTerms?.monthlyPayment ?? 0;
  const threeMonthsPITIA = monthlyPayment * 3;
  const sixMonthsPITIA = monthlyPayment * 6;

  const taxEscrowAtClose = (store.propertiesStore.annualTaxes / 12) * 3;
  const insuranceEscrowAtClose = (store.propertiesStore.annualInsurance / 12) * 3;

  const loanPurpose = option.loanPurpose ?? option.pricerLoanTerms?.loanPurpose;
  const isCashOut = loanPurpose === 'Cash-Out Refinance';
  const isPurchase = loanPurpose === 'Purchase';

  const debtOrPrice = isPurchase
    ? option.pricerLoanTerms?.acquisitionPrice ?? 0
    : isCashOut
      ? 0
      : option.pricerLoanTerms?.totalDebtPayoff ?? 0;
  const delta = debtOrPrice - loanAmount;

  const originationFee = (option.totalOriginationPoints ?? 0) * loanAmount / 100;
  const rocAndLenderFees =
    (option.totalProcessingFees || 0) +
    (option.totalAdminFees || 0) +
    (option.totalUnderwritingFees || 0) +
    (option.totalCommitmentFees || 0) +
    (option.totalBuydownFees || 0);

  const cashToClose = taxEscrowAtClose + insuranceEscrowAtClose + delta + originationFee + rocAndLenderFees;

  let thirdPartyClosingCosts = 0;
  if (loanAmount > 500000) {
    thirdPartyClosingCosts = loanAmount * 0.02;
  } else if (loanAmount > 200000) {
    thirdPartyClosingCosts = loanAmount * 0.03;
  } else if (loanAmount > 0) {
    thirdPartyClosingCosts = loanAmount * 0.04;
  }

  const legalFee = option.closingLegalReviewFee ?? store.pricerSummaryStore?.closingLegalReviewFee ?? 0;
  const cashToClosePlusThirdParty = cashToClose + thirdPartyClosingCosts + DEFAULT_FEE_CORELOGIX + legalFee;

  if (isCashOut) return sixMonthsPITIA;
  if (isPurchase) return cashToClosePlusThirdParty + threeMonthsPITIA;
  return cashToClosePlusThirdParty < 0 ? sixMonthsPITIA : cashToClosePlusThirdParty + sixMonthsPITIA;
};

const formatCurrency = value => {
  return value ? _formatCurrency(value) : 'N/A';
};

const formatPercentage = value => {
  return value ? _formatPercentage(value, 4) : 'N/A';
};

const formatDate = value => {
  return value ? _formatDate(value, 'MMMM d, yyyy') : 'N/A';
};

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'verdana, arial, sans-serif',
    lineHeight: '1.2',
    fontSize: '9.5pt',
    position: 'relative',

    ['& p']: {
      margin: '1em 0',
    },
    ['& li']: {
      margin: '0.5em 0',
      marginLeft: '2em',
    },
    ['& sup']: {
      fontSize: '80%',
      verticalAlign: 'super',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  table: {
    borderCollapse: 'collapse',
    ['& td']: {
      borderCollapse: 'collapse',
      padding: '5pt',
      border: '0.25pt solid ' + lighten(theme.palette.primary.dark, 0.5),
    },
    ['& th']: {
      color: 'black',
      textAlign: 'center',
      padding: '12px 0px',
    },
    ['& td:first-child']: {
      borderLeft: 0,
    },
    ['& td:last-child']: {
      borderRight: 0,
    },
  },
  thickRow: {
    borderTop: '2pt solid ' + lighten(theme.palette.primary.dark, 0.3),
  },
  header: {
    paddingBottom: '0.2in',
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  promoSubtitle: {
    fontSize: '16px',
    fontWeight: 700,
  },
  promoTitle: {
    color: '#285391',
    fontSize: '20px',
    fontWeight: 700,
    marginLeft: '10px',
  },
  promoBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(2, 136, 209, 0.08)',
    marginTop: '16px',
    padding: '16px',
  },
  headerDateTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    fontSize: '10pt',
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-line',
    minWidth: '90px',
    marginLeft: '24px',
  },
  protoTitleContainer: {
    display: 'flex',
  },
  logoContainer: {
    margin: 0,
    marginRight: '20px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  headerLine: {
    borderBottom: '4px solid ' + theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: '0',
    paddingRight: '0',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    justfyContent: 'flex-end',
    paddingTop: '0.2in',
  },
  title: {
    fontSize: '30pt',
    paddingBottom: '2px',
  },
  subtitle: {
    fontSize: '23pt',
    paddingBottom: '12px',
  },
  bold: {
    fontWeight: 'bold',
  },
  center: {
    textAlign: 'center',
  },
  darkBackground: {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'black',
  },
  note: {
    fontSize: '6pt',
    color: 'rgb(102 102 102)',
  },
  section: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '4pt 0',
    marginBottom: '15pt',
    fontSize: '15pt',
  },
  showOnPrint: {
    display: 'none',
  },
  pdfOnlyPage: {
    position: 'absolute',
    left: '-10000px',
    top: 0,
    width: '8.3in',
  },
  hideOnPrint: {
    display: 'block',
  },
  quotesTable: {
    ['& tr td']: {
      textAlign: 'center',
    },
    ['& tr td:first-child']: {
      fontWeight: 'bold',
      textAlign: 'left',
    },
  },
  fullWidth: {
    width: '100%',
  },
  labelColumn: {
    width: '25%',
  },
  informationSharedWithUsTable: {
    ['& td']: {
      width: '25%',
    },
  },
  warnings: {
    color: theme.palette.error.main,
  },
  date: {
    fontSize: '12pt',
    textAlign: 'right',
    '& div': {
      fontWeight: '700',
      paddingBottom: '8px',
    },
  },
  print: {
    //A4 size page
    width: '8.3in',
    height: '11.7in',

    //Add paddings
    padding: '0 0.65in',
    ['& $header']: {
      paddingTop: '0.6in',
    },

    ['& $footer']: {
      paddingBottom: '0.6in',
    },

    // Display hidden elements
    ['& $showOnPrint']: {
      display: 'flex',
    },
    ['& $pdfOnlyPage']: {
      position: 'relative',
      left: 'auto',
      top: 'auto',
      width: 'auto',
    },
    ['& $hideOnPrint']: {
      display: 'none',
    },
  },
  freeAppraisalText: {
    marginTop: '10px',
  },
}));

interface DownloadableQuoteHtmlProps {
  loanRatesData: any;
  store: QuoteTypeStore;
  pricingOptions: any;
}

export const DownloadableQuoteHtml = observer(
  forwardRef<any, DownloadableQuoteHtmlProps>(
    ({ loanRatesData, store, pricingOptions }, ref) => {
      const classes = useStyles();
      const lenderName = store.globalStore?.lenderInfo?.lenderTradeName;
      const loanQuoteData = store.getLoanQuoteData();

      const montlyInsurance = (loanRatesData?.grossAnnualInsurance ?? 0) / 12;
      const monthlyTaxes = (loanRatesData?.grossAnnualTaxes ?? 0) / 12;

      return (
        <PrintableArea printClass={classes.print} ref={ref}>
          <div className={classes.page}>
            <Header />
            <Content>
              <p>
                <b>{`Re: ${loanQuoteData.propertyAddress}`}</b>
              </p>
              <p>
                Thank you for considering {lenderName}! After reviewing the
                information provided, below is a summary of our proposed terms.
                Contact us with any questions, and we look forward to working
                with you!
              </p>
              <InformationSharedWithUs
                loanRatesData={loanRatesData}
                loanQuoteData={loanQuoteData}
                store={store}
              />
              <br />
            </Content>
            <Footer className={classes.showOnPrint} />
          </div>
          <div className={classes.page}>
            <Header className={classes.showOnPrint} />
            <Content>
              <TheConditionalTermsWeCanOffer
                store={store}
                data={loanRatesData}
                pricingOptions={pricingOptions}
              />
              <br />
              <div className={classes.hideOnPrint}>
                <MonthlyExpensesBreakDown
                  monthlyInsurance={montlyInsurance}
                  monthlyTaxes={monthlyTaxes}
                />
              </div>
              <br />
              <div className={classes.hideOnPrint}>
                <IndexAndRateLockInformation data={loanRatesData} />
              </div>
              <br />
            </Content>
            <Footer />
          </div>
          <div className={`${classes.page} ${classes.pdfOnlyPage}`}>
            <Header />
            <Content>
              <MonthlyExpensesBreakDown
                monthlyInsurance={montlyInsurance}
                monthlyTaxes={monthlyTaxes}
              />
              <br />
              <IndexAndRateLockInformation data={loanRatesData} />
              <br />
            </Content>
            <Footer />
          </div>
        </PrintableArea>
      );
    }
  )
);

interface InformationSharedWithUsProps {
  loanRatesData: any;
  loanQuoteData: LoanQuoteData;
  store: QuoteTypeStore;
}

const InformationSharedWithUs = ({
  loanRatesData,
  loanQuoteData,
  store,
}: InformationSharedWithUsProps) => {
  const classes = useStyles();
  const { loanSubtype, propertiesStore } = store;
  const property = propertiesStore?.getProperties()[0];
  const loanPurpose = property?.loanPurpose;

  return (
    <>
      <div className={classes.section}>Information Shared With Us:</div>
      <table
        className={clsx(classes.table, classes.informationSharedWithUsTable)}
      >
        <tr>
          <td className={classes.bold}>Property Address:</td>
          <td colSpan={3}>{loanQuoteData.propertyAddress}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Estimated As-Is Value*:</td>
          <td>{formatCurrency(loanQuoteData.totalEstimatedAsIsValue)}</td>
          <td className={classes.bold}>Property County:</td>
          <td>{loanQuoteData.propertyCounty}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Property Type:</td>
          <td>{Array.from(new Set(loanRatesData.propertyTypes)).join(', ')}</td>
          <td className={classes.bold}>Acquisition Date:</td>
          <td>{formatDate(loanQuoteData.acquisitionDate)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Total Acquisition Cost:</td>
          <td>{formatCurrency(loanQuoteData.totalAcquisitionCost)}</td>
          <td className={classes.bold}>Completed Renovations:</td>
          <td>{formatCurrency(loanQuoteData.completedRenovations)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Monthly Expenses:</td>
          <td>{formatCurrency(loanQuoteData.monthlyExpenses)}</td>
          <td className={classes.bold}>Monthly Rent*:</td>
          <td>{formatCurrency(loanQuoteData.monthlyRent)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Borrower’s Experience:</td>
          <td>{formatBorrowerExperience(loanQuoteData.borrowersExperience)}</td>
          <td className={classes.bold}>Estimated Credit Score:</td>
          <td>{loanQuoteData.estimatedCreditScore}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Citizenship Status:</td>
          <td>{loanQuoteData.citizenshipStatus}</td>
          <td className={classes.bold}>Payoff/Debt:</td>
          <td>{formatCurrency(property?.payoffAmount)}</td>
        </tr>
      </table>
      <div className={classes.note}>
        * Final As-Is and market rent values will be determined by third-party
        valuation reports satisfactory to Lender
      </div>
    </>
  );
};

const TheConditionalTermsWeCanOffer = observer(({ data, store, pricingOptions }) => {
  const classes = useStyles();
  const { pricerSummaryStore, loanDetailsStore, globalStore, loanSubtype } = store;
  const isEstimatedClosingCostEnabled =
    globalStore.userFeatures.isEstimatedClosingCostEnabled;

  const loanDetails = loanDetailsStore.getLoanDetails();

  const isTermLoan =
    loanSubtype === LoanSubType.SINGLE_PROPERTY ||
    loanSubtype === LoanSubType.RENTAL_PORTFOLIO;

  const getThirdPartyClosingCostFromLtv = option => {
    return (
      data?.values?.arm5?.[
        'ltv'.concat(mapLtvToValue(option.ltvBand).toString())
      ]?.thirdPartyClosingCost ?? 0
    );
  };

  const getSpreadOverIndex = option => {
    const { spreadRate } = option.rateRow?.[option.ltvBand] || {};
    return spreadRate ? `${spreadRate.toFixed(4)}%` : '';
  };

  const getTodayGrossRate = option => {
    const { spreadRate, swapRate } = option.rateRow?.[option.ltvBand] || {};
    return spreadRate && swapRate
      ? `${(spreadRate + swapRate).toFixed(4)}%`
      : '';
  };

  const getBuydownPoints = buydown => {
    const maxValueRatio = pricerSummaryStore.buydownRatio
      ? pricerSummaryStore.buydownRatio
      : pricerSummaryStore.buydownRatioStandard;
    return (buydown / maxValueRatio).toFixed(3);
  };

  function getTotalBuydownPoints(option) {
    const totalPoints =
      (option.totalBuydownFees / option.pricerLoanTerms.amount) * 100;
    return totalPoints.toFixed(3);
  }

  function arePricingOptionsEmpty(pricingOptions, field) {
    for (let option of pricingOptions) {
      if (option[field] && option[field] > 0) {
        return false;
      }
    }
    return true;
  }

  return (
    <>
      <div className={classes.section}>Compare Pricing Options:</div>
      <table className={clsx(classes.table, classes.quotesTable)}>
        <tr>
          <td style={{ width: '25%' }} />
          {pricingOptions.map((option, i) => (
            <td style={{ width: 75 / pricingOptions.length + '%' }}>
              <b>Option {i + 1}</b>
            </td>
          ))}
        </tr>
        <tr>
          <td>LTV</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatPercentage(option.formValues.loanToValue)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Loan Amount</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatCurrency(option.pricerLoanTerms.amount)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Loan Purpose</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.loanPurpose}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Amortization</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {
                  amortizationOptions.find(
                    opt => opt.value == option.formValues.amortization
                  )?.label
                }
              </Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Pre-Payment Penalty</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {
                  (pricerSummaryStore.prepaymentPenaltyOptions || []).find(
                    opt => opt.value == option.formValues.prePaymentPenalty
                  )?.label
                }
              </Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Rate Type</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.rateRow.rateType}</Box>
            </td>
          ))}
        </tr>
        {/*
        <tr>
          <td>Rate Buydown Fee</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{getBuydownPoints(option.formValues.rateBuydown)}%</Box>
            </td>
          ))}
        </tr>
          */}
        <tr>
          <td>Index</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {swapIndexLabels[option.rateRow[option.ltvBand].swapIndex]}
              </Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Spread Over Index</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{getSpreadOverIndex(option)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Today's Gross Rate</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{getTodayGrossRate(option)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Monthly Mortgage Payment (Principal and Interest Payments)</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {formatCurrency(
                  option.pricerLoanTerms.monthlyPaymentWithoutExpenses
                )}
              </Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>PITIA (Principal, Interest, Taxes, Insurance)</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {formatCurrency(option.pricerLoanTerms?.monthlyPayment) ?? '-'}
              </Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Post Loan Approval Rate Lock</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>14 Calendar Days</Box>
            </td>
          ))}
        </tr>
        {loanDetails.brokerPointsIn != null &&
          loanDetails.brokerPointsIn != '' &&
          loanDetails.brokerPointsIn !== 0 && (
            <tr>
              <td>Broker Fee</td>
              {pricingOptions.map((option, i) => (
                <td>
                  <Box>{formatPercentage(loanDetails.brokerPointsIn)}</Box>
                </td>
              ))}
            </tr>
          )}
        <tr>
          <td>Corelogix Fee</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatCurrency(DEFAULT_FEE_CORELOGIX)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Legal Review Fee</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatCurrency(option.closingLegalReviewFee)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Origination Points</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>
                {formatPercentage(option?.totalOriginationPoints)}
              </Box>
            </td>
          ))}
        </tr>
        {!arePricingOptionsEmpty(pricingOptions, 'totalProcessingFees') && (
          <tr>
            <td>Processing Fees</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatCurrency(option.totalProcessingFees)}</Box>
              </td>
            ))}
          </tr>
        )}
        {!arePricingOptionsEmpty(pricingOptions, 'totalAdminFees') && (
          <tr>
            <td>Admin Fees</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatCurrency(option.totalAdminFees)}</Box>
              </td>
            ))}
          </tr>
        )}
        {!arePricingOptionsEmpty(
          pricingOptions,
          'totalUnderwritingFees'
        ) && (
          <tr>
            <td>Underwriting Fees</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatCurrency(option.totalUnderwritingFees)}</Box>
              </td>
            ))}
          </tr>
        )}
        {!arePricingOptionsEmpty(pricingOptions, 'totalCommitmentFees') && (
          <tr>
            <td>Commitment Fees</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatCurrency(option.totalCommitmentFees)}</Box>
              </td>
            ))}
          </tr>
        )}
        {!arePricingOptionsEmpty(pricingOptions, 'totalBuydownFees') && (
          <tr>
            <td>Buydown Points</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatPercentage(getTotalBuydownPoints(option))}</Box>
              </td>
            ))}
          </tr>
        )}
        {isTermLoan && (
          <>
            <tr>
              <td>Liquidity Requirement:</td>
              {pricingOptions.map((option, i) => (
                <td key={i}>
                  <Box>
                    {store.pricerSummaryStore?.isCalculatingLiquidityForQuote
                      ? 'Calculating...'
                      : formatCurrency(store.pricerSummaryStore?.liquidityNeededForQuoteByOption?.[i])}
                  </Box>
                </td>
              ))}
            </tr>
          </>
        )}
        <tr>
          <td>DSCR (Debt Service Coverage Ratio)</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.dscr ?? '-'}</Box>
            </td>
          ))}
        </tr>
      </table>
    </>
  );
});

const IndexAndRateLockInformation = ({ data }) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.section}>Loan Information:</div>
      <table className={classes.table}>
        <tr className={classes.thickRow}>
          <td style={{ width: '25%' }}>
            <b>USD 3M Libor US Treasury Rates as of {data.todayDate}</b>
          </td>
          <td>
            5 Yr US Treasury: <b>{data.swapRateMap.fiveYrTsrySwap}%</b>
            <br />
            10 Yr US Treasury: <b>{data.swapRateMap.tenYrTsrySwap}%</b>
          </td>
        </tr>
        <tr>
          <td colSpan={2}>
            <ul>
              <li>
                Indicative Gross Rates are calculated as the sum of (1) value of
                the Index today + (2) Spread over Index.
                <b>
                  <u>
                    They represent today's rates, based on the information
                    submitted at pricing.
                  </u>
                </b>
              </li>
              <li>
                Gross rates are determined at the time of credit approval,
                calculated as the sum of the (1) value of the Index as of the
                date of credit approval, and (2) Spread over Index.
              </li>
              <li>
                50% of cash out can be used to satisfy liquidity requirement
              </li>
            </ul>
          </td>
        </tr>
      </table>
    </>
  );
};

interface MonthlyExpensesBreakDownProps {
  monthlyInsurance: number;
  monthlyTaxes: number;
}
const MonthlyExpensesBreakDown = ({
  monthlyInsurance,
  monthlyTaxes,
}: MonthlyExpensesBreakDownProps) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.section}>Monthly Expenses Breakdown:</div>
      <table
        className={clsx(
          classes.table,
          classes.fullWidth,
          classes.quotesTable ? classes.quotesTable : undefined
        )}
      >
        <tbody>
          <tr>
            <td className={classes.labelColumn}>
              <strong>Item</strong>
            </td>
            <td>
              <strong>Amount</strong>
            </td>
          </tr>
          <tr>
            <td>Property Taxes </td>
            <td>
              <Box>{formatCurrency(monthlyTaxes)}</Box>
            </td>
          </tr>
          <tr>
            <td>Insurance</td>
            <td>
              <Box>{formatCurrency(monthlyInsurance)}</Box>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const Header = ({ className = '', isQuote = false }) => {
  const classes = useStyles();
  const now = new Date();
  const timeString = now.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div className={`${classes.header} ${className}`}>
      <div className={classes.headerLine}>
        <div className={classes.headerLeft}>
          <div className={classes.logoContainer}>
            <LoginLogoImg
              containerClassName={classes.logoContainer}
              imgStyle={{ height: '0.95in' }}
            />
          </div>
          <div>
            <div className={classes.title}>
              Conditional <span className={classes.bold}>Quote</span>
            </div>
            <div className={classes.subtitle}>Rental Loan Program</div>
          </div>
        </div>
        <div className={classes.date}>
          <div>{formatToLocalDateTimeString(now, 'MM/dd/yyyy')}</div>
          <div>{timeString}</div>
        </div>
      </div>
    </div>
  );
};

const Footer = ({ className = '' }) => {
  const classes = useStyles();
  return (
    <div className={`${classes.footer} ${className}`}>
      <p className={classes.note}>
        Please note this letter of interest serves to outline the terms of the
        proposed financing of the referenced transaction.This letter is merely a
        general proposal, and is neither a binding offer, nor a contract.
        Borrower understands that no such offer will be forthcoming prior to
        completion of appropriate due diligence and underwriting performed
        and/or contracted by Lender. This proposal does not create any legally
        binding obligations on any party hereto. All properties are subject to
        satisfactory Lender due diligence underwriting including: satisfactory
        appraisal, satisfactory credit review of the borrowing entity and Key
        Principals, and satisfactory review of the property’s market and
        submarket.
      </p>
    </div>
  );
};
const Content = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};
